import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import { MapViewComponent } from '../../../../shared/map-view/map-view.component';
import {
  drawColumns,
  drawGroundPositionSelected,
  drawGroundPosition,
  drawRows,
  getHeight,
  getWidth
} from '../../../pigeonhole-page/pigeonhole-map-position-view/ground-cell';
import * as PIXI from 'pixi.js';

interface SetupGroundPigeonholeMapPosition {
  id: string;
  column: number;
  row: number;
  hasPallet: boolean;
  type: string;
  state: string;
  alias?: string;
  qrCode?: string;
}

@Component({
  selector: 'app-setup-ground-pigeonhole-map',
  templateUrl: './setup-ground-pigeonhole-map.component.html',
  styleUrls: ['./setup-ground-pigeonhole-map.component.scss']
})
export class SetupGroundPigeonholeMapComponent extends MapViewComponent implements OnInit, OnChanges {

  @Input() map: SetupGroundPigeonholeMapPosition[] = [];
  @Input() selectionMode = false;
  @Input() selectedPositionIds: string[] | Set<string> = [];
  @Output() positionClicked = new EventEmitter<SetupGroundPigeonholeMapPosition>();
  @Output() mapLoaded = new EventEmitter<void>();

  private maxY = 0;
  private selectedGraphics: PIXI.Container;

  constructor(
    protected elementRef: ElementRef,
    protected ngZone: NgZone
  ) {
    super(elementRef, ngZone);
  }

  ngOnInit(): void {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes.map?.currentValue) {
      if (!changes.map.currentValue?.length) {
        this.clearContent();
        this.clearSelectedPositions();
        this.mapLoaded.emit();
        return;
      }

      const gradePositions = this.getMaxCoordinates(changes.map.currentValue);
      const maxWidth = getWidth(gradePositions.positionsX.length);
      const maxHeight = getHeight(gradePositions.positionsY.length);
      this.maxY = gradePositions.positionsY.length - 1;

      if (!changes.map.previousValue) {
        this.initMap(maxWidth, maxHeight);
      }

      this.drawMap(changes.map.currentValue);
      this.mapLoaded.emit();
    }

    if (changes.selectedPositionIds?.currentValue || changes.selectionMode) {
      this.drawSelectedPositions(this.map);
    }
  }

  drawMap(groundPositions: SetupGroundPigeonholeMapPosition[]) {
    if (!groundPositions?.length) {
      return;
    }

    this.clearContent();
    this.drawGrades(groundPositions);
    this.drawDefaultMap(groundPositions);
    this.drawSelectedPositions(groundPositions);
  }

  drawGrades(groundPositions) {
    const gradePositions = this.getMaxCoordinates(groundPositions);
    const columns = drawColumns(gradePositions.positionsX);
    for (const c of columns) {
      // @ts-ignore
      this.viewPort.addChild(c);
    }

    const rows = drawRows(gradePositions.positionsY, this.maxY);
    for (const r of rows) {
      // @ts-ignore
      this.viewPort.addChild(r);
    }
  }

  drawDefaultMap(groundPositions: SetupGroundPigeonholeMapPosition[]) {
    for (const groundPosition of groundPositions) {
      const elements = drawGroundPosition(groundPosition as any, this.maxY);
      const area = elements[0];
      area.interactive = true;
      area.cursor = 'pointer';

      area.on('click', () => {
        if (!this.selectionMode) {
          return;
        }
        this.positionClicked.emit(groundPosition);
      });

      for (const e of elements) {
        // @ts-ignore
        this.viewPort.addChild(e);
      }
    }
  }

  private clearSelectedPositions() {
    if (this.selectedGraphics) {
      this.selectedGraphics.destroy();
      this.selectedGraphics = null;
    }
  }

  private getSelectedPositionIdsSet() {
    if (this.selectedPositionIds instanceof Set) {
      return new Set<string>(Array.from(this.selectedPositionIds));
    }

    if (Array.isArray(this.selectedPositionIds)) {
      return new Set<string>(this.selectedPositionIds);
    }

    return new Set<string>();
  }

  private drawSelectedPositions(groundPositions: SetupGroundPigeonholeMapPosition[]) {
    this.clearSelectedPositions();

    if (!this.selectionMode || !this.viewPort || !groundPositions?.length) {
      return;
    }

    const selectedIds = this.getSelectedPositionIdsSet();
    if (!selectedIds.size) {
      return;
    }

    this.selectedGraphics = new PIXI.Container();
    for (const groundPosition of groundPositions) {
      if (!selectedIds.has(groundPosition.id)) {
        continue;
      }

      const selectedElements = drawGroundPositionSelected(groundPosition as any, this.maxY);
      for (const element of selectedElements) {
        this.selectedGraphics.addChild(element);
      }
    }

    // @ts-ignore
    this.viewPort.addChild(this.selectedGraphics);
  }

  getMaxCoordinates(groundPositions: SetupGroundPigeonholeMapPosition[]) {
    const positionsX = [];
    const positionsY = [];
    let maxColumn = 0;
    let maxRow = 0;

    for (const gp of groundPositions) {
      if (gp.column > maxColumn) { maxColumn = gp.column; }
      if (gp.row > maxRow) { maxRow = gp.row; }
    }

    let countX = 0;
    while (countX <= maxColumn) {
      positionsX.push(countX);
      countX++;
    }

    let countY = 0;
    while (countY <= maxRow) {
      positionsY.push(countY);
      countY++;
    }

    return { positionsX, positionsY };
  }
}
